import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { ArrowLeft, Save, BookOpen } from 'lucide-react';
import ModalWrapper, { ModalCard } from '../../components/ModalWrapper';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import EffectTypePicker from './EffectTypePicker';
import { DEFAULT_EFFECT_TYPE } from '../../config/effectTypes';
import { VALENCE_KEYS, VALENCE_STYLE } from '../../config/valence';
import { gradientText } from '../../styles/mixins';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const TitleSection = styled.div`
  flex: 1;
`;

/* One accent per variant — a consequence is green, a blocker red, a loop amber.
   Adding a fourth kind of thing to the map should be a line here, not another
   ternary in five styled components. */
const ACCENT = {
  node: ['#4CAF50', '#45a049'],
  blocker: ['#EF5350', '#C62828'],
  loop: ['#D4A72C', '#A67C0B'],
};
const accent = (variant) => ACCENT[variant] || ACCENT.node;

const Title = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  ${(props) => {
    const [from, to] = accent(props.$variant);
    return gradientText(from, to, from);
  }}
`;

const Subtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
`;

const ParentNodeContext = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
`;

const ParentNodeLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const ParentNodeTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
`;

const ParentNodeDescription = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
`;


const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
`;

const TextInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 1rem;
  color: white;
  font-family: inherit;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: ${(props) => accent(props.$variant)[0]};
    background: rgba(255, 255, 255, 0.12);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 0.95rem;
  color: white;
  font-family: inherit;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: ${(props) => accent(props.$variant)[0]};
    background: rgba(255, 255, 255, 0.12);
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }
`;

const SaveButton = styled.button`
  background: ${(props) => `linear-gradient(135deg, ${accent(props.$variant)[0]} 0%, ${accent(props.$variant)[1]} 100%)`};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;

  &:hover {
    background: linear-gradient(135deg, #45a049 0%, #3d8b40 100%);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  html[data-platform='tizen'] & {
    background: ${(props) => accent(props.$variant)[0]};
  }

  html[data-platform='tizen'] &:hover {
    background: ${(props) => accent(props.$variant)[1]};
  }
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
`;

const Hint = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

/* The same chip the node modal uses for judging a node, so a valence looks
   the same whether it is given while writing or afterwards. */
const Chip = styled.button`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1.5px solid ${(p) => (p.$on ? p.$color : 'rgba(255,255,255,0.18)')};
  background: ${(p) => (p.$on ? p.$bg : 'transparent')};
  color: ${(p) => (p.$on ? p.$color : 'rgba(255,255,255,0.75)')};
  font: inherit;
  font-size: 0.85rem;
  font-weight: ${(p) => (p.$on ? 700 : 500)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover { border-color: ${(p) => p.$color}; }
  &:focus-visible { outline: 2px solid ${(p) => p.$color}; outline-offset: 2px; }
`;

const ReferencesSection = styled.div`
  background: rgba(33, 150, 243, 0.08);
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
`;

const ReferencesTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ReferenceItem = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  padding: 4px 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;

  &:before {
    content: '📄';
    font-size: 0.7rem;
  }
`;

/**
 * CreateNodeModal - Step 3: Create new node
 *
 * Simple form for creating a new node with:
 * - Title (pre-filled from user input or AI suggestion)
 * - Description (pre-filled if from AI suggestion)
 * - References (shown if from AI suggestion)
 *
 * Mood will be automatically detected by AI from the content.
 */
export default function CreateNodeModal({
  visible = false,
  initialTitle = '',
  initialDescription = '',
  references = [],
  parentNode,
  onSave,
  onBack,
  onClose,
  titleMaxChars = 70,
  // Impact Path v2: ask how this consequence follows from its parent
  // (specs/impact-path-v2-prd.md §3). Off by default, so v1 is unchanged.
  showEffectType = false,
  initialEffectType = DEFAULT_EFFECT_TYPE,
  // 'node' | 'blocker'. A blocker is written the same way a consequence is —
  // same dialog, same fields, red instead of green — so the map has one way of
  // adding things rather than two (specs/impact-path-v2-prd.md §4).
  variant = 'node',
  title = null,
  subtitle = null,
  parentLabel = null,
  saveLabel = null,
  // Impact Path v2 (specs/impact-path-v2-prd.md §4b): judge the node while
  // writing it — toivottu / ei-toivottu / epäselvä. Off by default.
  showValence = false,
  initialValence = null,
  // v2 add-first flow (§4f): the title may be left empty — the description
  // is the thing written, and the AI condenses a title once the node is on
  // the map. The description is then the field that takes focus.
  titleOptional = false,
  // What the card is in the chain (specs/tki-kuutio-flow.md): a condition the project
  // created, an outcome observed, or just an output. Off by default; sent as `kind`.
  showKind = false,
  kinds = ['output', 'condition', 'outcome'],
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [effectType, setEffectType] = useState(initialEffectType);
  const [valence, setValence] = useState(initialValence);
  const [kind, setKind] = useState(null);

  // Update fields when modal opens with new values
  useEffect(() => {
    if (visible) {
      setText(initialTitle || '');
      setDescription(initialDescription || '');
      setEffectType(initialEffectType);
      setValence(initialValence);
      setKind(null);
    }
  }, [visible, initialTitle, initialDescription, initialEffectType, initialValence]);

  const canSave = titleOptional ? !!(text.trim() || description.trim()) : !!text.trim();

  const handleSave = () => {
    // The save button is disabled while !canSave, so this is only a guard
    if (!canSave) return;

    onSave({
      text: text.trim(),
      description: description.trim(),
      // Only sent when the picker was shown — v1 callers keep the old payload.
      ...(showEffectType ? { effectType } : {}),
      ...(showValence ? { valence } : {}),
      ...(showKind ? { kind } : {}),
      // Mood will be auto-detected by AI based on title + description
    });

    // Reset form
    setText(initialTitle);
    setDescription('');
  };

  return (
    <ModalWrapper visible={visible} onClose={onClose} zIndex={1001} opacity={0.85}>
      <ModalCard size="md" onClick={e => e.stopPropagation()} style={{ padding: '30px' }}>
        <Header>
          <TitleSection>
            <Title $variant={variant}>{title || t('impactPath.createNode.title')}</Title>
            <Subtitle>{subtitle || t('impactPath.createNode.subtitle')}</Subtitle>
          </TitleSection>
        </Header>

        {/* Show parent node context with title and description */}
        {parentNode && (
          <ParentNodeContext>
            <ParentNodeLabel>{parentLabel || t('impactPath.createNode.parentLabel')}</ParentNodeLabel>
            <ParentNodeTitle>{parentNode.text || parentNode.title}</ParentNodeTitle>
            {(() => {
              const desc = parentNode.description || parentNode.fullText || parentNode.ai_analysis || '';
              if (desc && desc !== 'None' && desc !== 'null' && desc.trim()) {
                return <ParentNodeDescription>{desc}</ParentNodeDescription>;
              }
              return null;
            })()}
          </ParentNodeContext>
        )}

        {/* The type was chosen in the step before this dialog; here it is one
            line that can be changed, not a second form to fill in. */}
        {showEffectType && (
          <div style={{ marginBottom: 16 }}>
            <EffectTypePicker collapsible value={effectType} onChange={setEffectType} />
          </div>
        )}

        <Form>
          <FormGroup>
            <LabelRow>
              <Label>{titleOptional ? t('impactPath.createNode.titleLabelOptional') : t('impactPath.createNode.titleLabel')}</Label>
              <Hint>{titleOptional && !text.length ? t('impactPath.createNode.titleHintAi') : `${text.length} / ${titleMaxChars}`}</Hint>
            </LabelRow>
            <TextInput
              $variant={variant}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={titleOptional ? t('impactPath.createNode.titlePlaceholderAi') : t('impactPath.createNode.titlePlaceholder')}
              autoFocus={!titleOptional}
              maxLength={titleMaxChars}
            />
          </FormGroup>

          <FormGroup>
            <LabelRow>
              <Label>{t('impactPath.createNode.descriptionLabel')}</Label>
              {titleOptional && <Hint>{t('impactPath.createNode.descriptionHintLong')}</Hint>}
            </LabelRow>
            <TextArea
              $variant={variant}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('impactPath.createNode.descriptionPlaceholder')}
              autoFocus={titleOptional}
              style={titleOptional ? { minHeight: 140 } : undefined}
            />
          </FormGroup>

          {showValence && (
            <FormGroup>
              <LabelRow>
                <Label>{t('impactPath.valence.label')}</Label>
                <Hint>{t('impactPath.createNode.optional')}</Hint>
              </LabelRow>
              <Chips role="group" aria-label={t('impactPath.valence.label')}>
                {VALENCE_KEYS.map((key) => {
                  const vs = VALENCE_STYLE[key];
                  const on = valence === key;
                  return (
                    <Chip key={key} type="button" $on={on} $color={vs.color} $bg={vs.tint} aria-pressed={on}
                          onClick={() => setValence(on ? null : key)}>
                      {vs.mark && <span aria-hidden="true">{vs.mark}</span>}{vs.label}
                    </Chip>
                  );
                })}
              </Chips>
            </FormGroup>
          )}

          {showKind && (
            <FormGroup>
              <LabelRow>
                <Label>{t('impactPath.addNode.kindLabel')}</Label>
                <Hint>{t('impactPath.createNode.optional')}</Hint>
              </LabelRow>
              <Chips role="group" aria-label={t('impactPath.addNode.kindLabel')}>
                {kinds.map((key) => {
                  const on = kind === key;
                  return (
                    <Chip key={key} type="button" $on={on} $color="#4cc7ce" $bg="rgba(76, 199, 206, 0.16)" aria-pressed={on}
                          onClick={() => setKind(on ? null : key)}>
                      {t(`impactPath.scene.kind.${key}`)}
                    </Chip>
                  );
                })}
              </Chips>
            </FormGroup>
          )}

          {references.length > 0 && (
            <ReferencesSection>
              <ReferencesTitle>
                <BookOpen size={14} />
                {t('impactPath.createNode.researchSources', { count: references.length })}
              </ReferencesTitle>
              {references.map((ref, i) => (
                <ReferenceItem key={i}>{ref}</ReferenceItem>
              ))}
            </ReferencesSection>
          )}
        </Form>

        <Footer>
          {onBack && (
            <BackButton onClick={onBack}>
              <ArrowLeft size={18} />
              {t('impactPath.createNode.back')}
            </BackButton>
          )}
          <SaveButton $variant={variant} onClick={handleSave} disabled={!canSave}>
            <Save size={18} />
            {saveLabel || t('impactPath.createNode.save')}
          </SaveButton>
        </Footer>
      </ModalCard>
    </ModalWrapper>
  );
}
